import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

export default function Tooltip({
  title,
  description,
  content,
  children,
  className = '',
  placement = 'top',
  as: Tag = 'div',
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const rootRef = useRef(null);

  const hasContent = Boolean(title || description || content);

  const openTooltip = useCallback(() => {
    if (rootRef.current) setRect(rootRef.current.getBoundingClientRect());
    setOpen(true);
  }, []);

  const closeTooltip = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) closeTooltip();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    // Il box è portato fuori dal contenitore (tabelle scrollabili, card con
    // overflow, ecc.): non segue lo scroll, quindi lo chiudiamo invece di
    // lasciarlo "staccato" dal trigger.
    window.addEventListener('scroll', closeTooltip, true);
    window.addEventListener('resize', closeTooltip);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      window.removeEventListener('scroll', closeTooltip, true);
      window.removeEventListener('resize', closeTooltip);
    };
  }, [open, closeTooltip]);

  return (
    <Tag
      ref={rootRef}
      className={`app-tooltip ${className}`.trim()}
      onMouseEnter={hasContent ? openTooltip : undefined}
      onMouseLeave={hasContent ? closeTooltip : undefined}
      onClick={hasContent ? () => (open ? closeTooltip() : openTooltip()) : undefined}
      {...rest}
    >
      {children}
      {hasContent && open && rect && typeof document !== 'undefined' && createPortal(
        <span
          className={`app-tooltip__box app-tooltip__box--${placement}`}
          role="tooltip"
          style={{
            top: placement === 'bottom' ? rect.bottom : rect.top,
            left: rect.left + rect.width / 2,
          }}
        >
          {title && <strong>{title}</strong>}
          {description}
          {content}
        </span>,
        document.body
      )}
    </Tag>
  );
}
